// Shared Gantt utility functions used by both the single-flowchart Gantt
// and the project-level Gantt view.

import { calcSchedule } from '../lib/gantt';

/** A named, saved "Set timeline dates" range (GanttPanel.tsx) — stored per
 *  flowchart (gantt_timeline_presets column) and shared with every collaborator,
 *  unlike the single currently-active timelineView (also DB-backed, but there's
 *  only ever one of those at a time). */
export interface TimelinePreset {
  id: string;
  name: string;
  start: string; // ISO date, same format as timelineView.start
  end: string;   // ISO date, same format as timelineView.end
}

/** A named, saved "Filtered Export" row selection (GanttPanel.tsx) — stored per
 *  flowchart (gantt_export_row_presets column) and shared with every collaborator,
 *  same convention as TimelinePreset above. `boxIds` are top-level box ids only —
 *  the export this feeds never includes subitems or Area-toplevel rows.
 *
 *  timelineStart/timelineEnd (optional, added 2026-10-01): the "Set Timeline Dates"
 *  range that was active when this preset was saved. Without this, a preset's rows
 *  would export fine but with blank (bar-less) rows for any task whose bar falls
 *  outside whatever timeline crop happens to be the CURRENT shared one at the
 *  moment someone else applies the preset — confusing, since the preset's whole
 *  point is "export exactly these tasks" and a leftover, unrelated date crop
 *  shouldn't silently clip them. Applying a preset restores this range the same
 *  way it restores row order/selection: as a local, mode-scoped override (see
 *  GanttPanel.tsx's exportTimelineOverride) — never by overwriting the shared,
 *  DB-persisted timelineView itself, which would silently reclip the Gantt for
 *  every other collaborator currently looking at it. */
export interface ExportRowPreset {
  id: string;
  name: string;
  boxIds: number[];
  timelineStart?: string;
  timelineEnd?: string;
}

export interface GanttBox {
  id: number;
  text: string;   // display text (matches Box.text from FlowchartBuilder)
  type?: string;
  duration?: string;
  assignee?: string;
  departmentColor?: string;
  completion?: number;
  importantFinish?: boolean;
}

export interface GanttArrow {
  fromBox: number;
  toBox: number;
}

export interface GanttTask extends Omit<GanttBox, 'duration'> {
  start: number;    // offset in duration-units from t=0
  duration: number; // computed duration in gantt units
}

export interface CalendarHeaders {
  // weeks mode
  quarters?: { label: string; span: number }[];
  // days + weeks mode
  months?: { label: string; span: number }[];
  // weeks mode: raw week numbers. days mode: grouped week spans.
  weeks?: { quarter: string; month: string; week: number }[] | { label: string; span: number }[];
  // days mode: raw day data. hours mode: grouped day spans.
  days?: { month: string; week: number; day: number; dayName: string; isWeekday: boolean }[] | { label: string; span: number }[];
  // hours mode
  hours?: { hour: string; isWorkingHour: boolean; isWeekday: boolean }[];
}

export function calcGanttForBoxes(boxes: GanttBox[], arrows: GanttArrow[]): GanttTask[] {
  return calcSchedule(boxes, arrows) as GanttTask[];
}

const MONTH_NAMES = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const DAY_NAMES   = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

export function generateCalendarHeaders(
  maxDuration: number,
  unit: string,
  startDate: Date,
  workHoursOnly: boolean
): CalendarHeaders {
  const date = new Date(startDate);

  if (unit === 'hours') {
    const hourData: { date: string; dayName: string; month: string; hour: string; isWorkingHour: boolean; isWeekday: boolean }[] = [];
    let currentHour = 0;
    while (hourData.length < maxDuration) {
      const cur = new Date(date);
      cur.setHours(cur.getHours() + currentHour);
      const dow = cur.getDay();
      const h = cur.getHours();
      const isWorkingHour = h >= 9 && h < 17;
      const isWeekday = dow >= 1 && dow <= 5;
      if (workHoursOnly && (!isWeekday || !isWorkingHour)) { currentHour++; continue; }
      hourData.push({
        date: cur.toLocaleDateString('en-US', { day: 'numeric', month: 'short' }),
        dayName: DAY_NAMES[dow],
        month: MONTH_NAMES[cur.getMonth()],
        hour: `${h}:00`,
        isWorkingHour: isWorkingHour && isWeekday,
        isWeekday,
      });
      currentHour++;
    }
    return {
      months: groupSpans(hourData, h => h.month),
      days:   groupSpans(hourData, h => h.date).map((s, _i, _a) => ({ ...s, label: s.label })) as any,
      hours:  hourData,
    };
  }

  if (unit === 'days') {
    const dayData: { month: string; week: number; day: number; dayName: string; isWeekday: boolean }[] = [];
    let d = 0;
    while (dayData.length < maxDuration) {
      const cur = new Date(date);
      cur.setDate(cur.getDate() + d);
      const dow = cur.getDay();
      const isWeekday = dow >= 1 && dow <= 5;
      if (workHoursOnly && !isWeekday) { d++; continue; }
      const startOfYear = new Date(cur.getFullYear(), 0, 1);
      const past = (cur.getTime() - startOfYear.getTime()) / 86400000;
      const week = Math.ceil((past + startOfYear.getDay() + 1) / 7);
      dayData.push({ month: MONTH_NAMES[cur.getMonth()], week, day: cur.getDate(), dayName: DAY_NAMES[dow], isWeekday });
      d++;
    }
    return {
      months: groupSpans(dayData, d => d.month),
      weeks:  groupSpans(dayData, d => String(d.week)),
      days:   dayData as any,
    };
  }

  // weeks
  const weekData: { quarter: string; month: string; week: number }[] = [];
  for (let i = 0; i < maxDuration; i++) {
    const cur = new Date(date);
    cur.setDate(cur.getDate() + i * 7);
    const year = cur.getFullYear().toString().slice(-2);
    const m = cur.getMonth();
    const q = Math.floor(m / 3) + 1;
    const startOfYear = new Date(cur.getFullYear(), 0, 1);
    const past = (cur.getTime() - startOfYear.getTime()) / 86400000;
    const week = Math.ceil((past + startOfYear.getDay() + 1) / 7);
    weekData.push({ quarter: `${year}Q${q}`, month: MONTH_NAMES[m], week });
  }
  return {
    quarters: groupSpans(weekData, w => w.quarter),
    months:   groupSpans(weekData, w => w.month),
    weeks:    weekData as any,
  };
}

function groupSpans<T>(arr: T[], key: (item: T) => string): { label: string; span: number }[] {
  const result: { label: string; span: number }[] = [];
  arr.forEach((item, i) => {
    const k = key(item);
    if (i === 0 || k !== result[result.length - 1].label) {
      result.push({ label: k, span: 1 });
    } else {
      result[result.length - 1].span++;
    }
  });
  return result;
}
